import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { TextInput } from '../../components/ui/form/TextInput';
import { Verified } from '../../components/ui/Verified';
import { ApiError } from '../../lib/api';
import { playlists, type Collaborator, type PlaylistDetail } from './api';

/**
 * The people helping keep a playlist, and the owner's way to ask another.
 *
 * ## What they may do
 *
 * Add, remove and reorder. Not rename, not re-cover, not change who can
 * see it, not delete it, and not invite anybody else - the playlist stays
 * the owner's, and only its contents are shared. Said out loud here
 * because it is the first thing anybody wonders before pressing Invite.
 *
 * ## Asked, not added
 *
 * An invitation is a notification with two answers, so nobody's name
 * appears on a playlist they never agreed to. Until it is accepted only
 * the owner sees that it was sent.
 */
export function Collaborators({
  playlist,
  onChanged,
}: {
  playlist: PlaylistDetail;
  onChanged: () => void;
}) {
  const client = useQueryClient();
  const [username, setUsername] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const invite = useMutation({
    mutationFn: (name: string) => playlists.invite(playlist.id, name),
    onSuccess: () => {
      setUsername('');
      setProblem(null);
      onChanged();
      void client.invalidateQueries({ queryKey: ['playlist', playlist.id] });
    },
    onError: (error) =>
      setProblem(
        error instanceof ApiError
          ? (error.fieldErrors.username ?? error.message)
          : 'That did not go through.',
      ),
  });

  const remove = useMutation({
    mutationFn: (userId: number) => playlists.removeCollaborator(playlist.id, userId),
    onSuccess: onChanged,
  });

  const people = playlist.collaborators;

  // Nothing to say on somebody else's playlist that nobody else keeps.
  if (!playlist.is_owner && people.length === 0) return null;

  return (
    <Card className="flex flex-col gap-3 p-4" data-collaborators>
      <div className="flex items-center justify-between gap-3">
        <h2 className="meta-caps text-faint">
          Kept together
          {people.length > 0 && <span className="ml-2 text-ghost">{people.length}</span>}
        </h2>

        {playlist.is_owner && (
          <button
            type="button"
            onClick={() => setOpen((was) => !was)}
            data-collaborators-toggle={open ? 'open' : 'shut'}
            className="text-[13px] text-soft transition-colors hover:text-ink"
          >
            {open ? 'Done' : 'Add somebody'}
          </button>
        )}
      </div>

      {people.length === 0 ? (
        <p className="text-[13px] text-faint">
          Only you. Anybody you add can put tracks on this playlist, take them off and change the
          order; the name, the cover and who can see it stay yours.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-collaborator-list>
          {people.map((person) => (
            <Row
              key={person.id}
              person={person}
              canRemove={playlist.is_owner}
              onRemove={() => remove.mutate(person.id)}
            />
          ))}
        </ul>
      )}

      {open && playlist.is_owner && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();

            const name = username.trim().replace(/^@/, '');

            if (name !== '') invite.mutate(name);
          }}
        >
          <div className="flex items-center gap-2">
            <TextInput
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="username"
              aria-label="Who to add"
              data-collaborator-username
              className="max-w-xs"
            />
            <Button type="submit" loading={invite.isPending} disabled={username.trim() === ''}>
              Invite
            </Button>
          </div>
          <p className="meta text-ghost">
            They get an invitation to accept. Until they do, nothing about the playlist changes.
          </p>
          {problem !== null && <p className="text-[13px] text-negative">{problem}</p>}
        </form>
      )}
    </Card>
  );
}

function Row({
  person,
  canRemove,
  onRemove,
}: {
  person: Collaborator;
  canRemove: boolean;
  onRemove: () => void;
}) {
  return (
    <li className="flex items-center gap-2.5" data-collaborator={person.id}>
      <Avatar src={person.avatar} name={person.display_name} size={28} />
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
        {person.display_name}
        {person.verified && <Verified />}
        <span className="ml-1.5 text-faint">@{person.username}</span>
      </span>

      {person.status === 'invited' && <span className="meta shrink-0 text-ghost">asked</span>}

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${person.display_name}`}
          data-collaborator-remove={person.id}
          className="shrink-0 rounded px-1.5 py-1 font-mono text-[11px] text-ghost transition-colors hover:text-hot-text"
        >
          ×
        </button>
      )}
    </li>
  );
}

/**
 * The invitation itself, on the playlist it is for.
 *
 * Above the tracks rather than in a dialog: the question "do you want to
 * help keep this" is best answered while looking at what is in it.
 */
export function CollaborationInvite({
  playlist,
  onAnswered,
}: {
  playlist: PlaylistDetail;
  onAnswered: () => void;
}) {
  const accept = useMutation({
    mutationFn: () => playlists.acceptInvite(playlist.id),
    onSuccess: onAnswered,
  });

  const decline = useMutation({
    mutationFn: () => playlists.removeCollaborator(playlist.id),
    onSuccess: onAnswered,
  });

  if (playlist.my_collaboration !== 'invited') return null;

  return (
    <Card className="flex flex-wrap items-center gap-3 p-4" data-collaboration-invite>
      <p className="min-w-0 flex-1 text-sm text-soft">
        <span className="font-medium text-ink">
          {playlist.owner?.display_name ?? playlist.owner?.username ?? 'Somebody'}
        </span>{' '}
        asked you to help keep this playlist. You would be able to add tracks, take them off and
        change the order.
      </p>
      <Button loading={accept.isPending} data-collaboration-accept onClick={() => accept.mutate()}>
        Accept
      </Button>
      <Button
        variant="ghost"
        loading={decline.isPending}
        data-collaboration-decline
        onClick={() => decline.mutate()}
      >
        No thanks
      </Button>
    </Card>
  );
}
